import * as XLSX from 'xlsx';
import { isBdApiEnabled } from 'config/bdApiConfig';
import {
  cacheGet,
  cacheSet,
  commonSaveList,
  commonSelect,
  diffRowsByUid,
} from 'services/commonQuery';

/**
 * 기준정보 — 거래처 마스터
 *
 * - 헤더(vendor) 1 : 계좌(account) N
 * - API(isBdApiEnabled): CmVendor · 아니면 localStorage
 * - 문서(품의·결의 등)는 optional vendorUid + 스냅샷 사용 (본 스토어는 카탈로그)
 * - 엑셀은 평탄 1시트로 업로드·양식 제공 (그룹키=식별번호)
 *
 * 키: erp.master.vendor|{tenant}
 *     erp.master.vendorAccount|{tenant}
 */

const vendorCache = new Map();
const accountCache = new Map();

export const SAVE_QUERY_VENDOR = [
  'CmVendor.insertVendor',
  'CmVendor.updateVendor',
  'CmVendor.deleteVendor',
];
export const SAVE_QUERY_VENDOR_ACCOUNT = [
  'CmVendor.insertVendorAccount',
  'CmVendor.updateVendorAccount',
  'CmVendor.deleteVendorAccount',
];

export const VENDOR_STORAGE_PREFIX = 'erp.master.vendor';
export const VENDOR_ACCOUNT_STORAGE_PREFIX = 'erp.master.vendorAccount';

/** 파티 종류 — 식별키 체계 */
export const PARTY_KIND = {
  PERSON: 'PERSON',
  BIZ: 'BIZ',
  ORG: 'ORG',
};

export const PARTY_KIND_OPTIONS = [
  { value: PARTY_KIND.PERSON, label: '개인' },
  { value: PARTY_KIND.BIZ, label: '사업자' },
  { value: PARTY_KIND.ORG, label: '단체·기관' },
];

/** 거래처 구분 — 화면·필드 분기용 */
export const VENDOR_TYPE = {
  INDIVIDUAL: 'INDIVIDUAL',
  SOLE_PROP: 'SOLE_PROP',
  CORP_PROFIT: 'CORP_PROFIT',
  CORP_NONPROFIT: 'CORP_NONPROFIT',
  RELIGIOUS: 'RELIGIOUS',
  GOV: 'GOV',
  FINANCE: 'FINANCE',
  FOREIGN: 'FOREIGN',
  OTHER: 'OTHER',
};

export const VENDOR_TYPE_OPTIONS = [
  { value: VENDOR_TYPE.INDIVIDUAL, label: '개인' },
  { value: VENDOR_TYPE.SOLE_PROP, label: '개인사업자' },
  { value: VENDOR_TYPE.CORP_PROFIT, label: '영리법인' },
  { value: VENDOR_TYPE.CORP_NONPROFIT, label: '비영리·공익' },
  { value: VENDOR_TYPE.RELIGIOUS, label: '종교단체' },
  { value: VENDOR_TYPE.GOV, label: '국가·지자체' },
  { value: VENDOR_TYPE.FINANCE, label: '금융기관' },
  { value: VENDOR_TYPE.FOREIGN, label: '외국법인' },
  { value: VENDOR_TYPE.OTHER, label: '기타' },
];

export const ID_TYPE = {
  BIZ: 'BIZ',
  RRN: 'RRN',
  FOREIGN: 'FOREIGN',
  NONE: 'NONE',
};

export const ID_TYPE_OPTIONS = [
  { value: ID_TYPE.BIZ, label: '사업자번호' },
  { value: ID_TYPE.RRN, label: '주민등록번호' },
  { value: ID_TYPE.FOREIGN, label: '외국인·해외' },
  { value: ID_TYPE.NONE, label: '없음(임시)' },
];

export const VENDOR_STATUS = {
  ACTIVE: 'ACTIVE',
  CANDIDATE: 'CANDIDATE',
  DORMANT: 'DORMANT',
  ARCHIVED: 'ARCHIVED',
};

export const VENDOR_STATUS_OPTIONS = [
  { value: VENDOR_STATUS.ACTIVE, label: '사용' },
  { value: VENDOR_STATUS.CANDIDATE, label: '후보' },
  { value: VENDOR_STATUS.DORMANT, label: '휴면' },
  { value: VENDOR_STATUS.ARCHIVED, label: '보관' },
];

export const YN_OPTIONS = [
  { value: 'Y', label: 'Y' },
  { value: 'N', label: 'N' },
];

/** 엑셀 평탄 시트 헤더 (업로드·양식 동일) */
export const VENDOR_FLAT_EXCEL_HEADERS = [
  '거래처코드',
  '거래처명',
  '거래처구분',
  '식별유형',
  '식별번호',
  '대표자',
  '전화',
  '이메일',
  '주소',
  '상태',
  '사용',
  '비고',
  '은행코드',
  '은행명',
  '계좌번호',
  '예금주',
  '대표계좌',
  '계좌사용',
  '계좌비고',
];

function labelOf(options, code) {
  return options.find((o) => o.value === code)?.label || code || '';
}

export function partyKindLabel(code) {
  return labelOf(PARTY_KIND_OPTIONS, code);
}

export function vendorTypeLabel(code) {
  return labelOf(VENDOR_TYPE_OPTIONS, code);
}

export function idTypeLabel(code) {
  return labelOf(ID_TYPE_OPTIONS, code);
}

export function vendorStatusLabel(code) {
  return labelOf(VENDOR_STATUS_OPTIONS, code);
}

export function createUid(prefix = 'VND') {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

export function createVendorUid() {
  return createUid('VND');
}

export function createAccountUid() {
  return createUid('VAC');
}

export function vendorStorageKey(tenantCode) {
  return `${VENDOR_STORAGE_PREFIX}|${tenantCode || ''}`;
}

export function accountStorageKey(tenantCode) {
  return `${VENDOR_ACCOUNT_STORAGE_PREFIX}|${tenantCode || ''}`;
}

/** 숫자만 추출 */
export function digitsOnly(v) {
  return String(v || '').replace(/\D/g, '');
}

/** 사업자번호 표시 XXX-XX-XXXXX */
export function formatBizNo(v) {
  const d = digitsOnly(v);
  if (d.length !== 10) return String(v || '').trim();
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

/** 사업자번호 체크디지트 */
export function isValidBizNo(v) {
  const d = digitsOnly(v);
  if (d.length !== 10) return false;
  const nums = d.split('').map((n) => parseInt(n, 10));
  const weights = [1, 3, 7, 1, 3, 7, 1, 3, 5];
  let sum = 0;
  for (let i = 0; i < 9; i += 1) sum += weights[i] * nums[i];
  sum += Math.floor((weights[8] * nums[8]) / 10);
  const check = (10 - (sum % 10)) % 10;
  return check === nums[9];
}

/**
 * 사업자번호 가운데 2자리 → 거래처구분·파티·식별유형 추정
 * @returns {{ vendorType, partyKind, idType } | null}
 */
export function inferVendorTypeFromBizNo(bizNo) {
  const d = digitsOnly(bizNo);
  if (d.length !== 10) return null;
  const mid = parseInt(d.slice(3, 5), 10);
  if (Number.isNaN(mid)) return null;

  if (mid >= 1 && mid <= 79) {
    return { vendorType: VENDOR_TYPE.SOLE_PROP, partyKind: PARTY_KIND.BIZ, idType: ID_TYPE.BIZ };
  }
  if (mid >= 90 && mid <= 99) {
    return { vendorType: VENDOR_TYPE.SOLE_PROP, partyKind: PARTY_KIND.BIZ, idType: ID_TYPE.BIZ };
  }
  if ([81, 86, 87, 88, 85].includes(mid)) {
    return { vendorType: VENDOR_TYPE.CORP_PROFIT, partyKind: PARTY_KIND.BIZ, idType: ID_TYPE.BIZ };
  }
  if (mid === 82) {
    return { vendorType: VENDOR_TYPE.CORP_NONPROFIT, partyKind: PARTY_KIND.ORG, idType: ID_TYPE.BIZ };
  }
  if (mid === 83) {
    return { vendorType: VENDOR_TYPE.GOV, partyKind: PARTY_KIND.ORG, idType: ID_TYPE.BIZ };
  }
  if (mid === 84) {
    return { vendorType: VENDOR_TYPE.FOREIGN, partyKind: PARTY_KIND.BIZ, idType: ID_TYPE.FOREIGN };
  }
  if (mid === 89) {
    return { vendorType: VENDOR_TYPE.RELIGIOUS, partyKind: PARTY_KIND.ORG, idType: ID_TYPE.BIZ };
  }
  if (mid === 80) {
    return { vendorType: VENDOR_TYPE.OTHER, partyKind: PARTY_KIND.ORG, idType: ID_TYPE.BIZ };
  }
  return { vendorType: VENDOR_TYPE.OTHER, partyKind: PARTY_KIND.BIZ, idType: ID_TYPE.BIZ };
}

/** 식별번호 정규화 (저장·unique용) */
export function normalizeIdNo(idType, idNo) {
  const raw = String(idNo || '').trim();
  if (!raw) return '';
  if (idType === ID_TYPE.BIZ || idType === ID_TYPE.RRN) return digitsOnly(raw);
  return raw.replace(/\s+/g, '');
}

export function emptyVendor(partial = {}) {
  return {
    vendorUid: '',
    vendorCode: '',
    vendorName: '',
    partyKind: PARTY_KIND.BIZ,
    vendorType: VENDOR_TYPE.SOLE_PROP,
    vendorTypeSource: 'MANUAL',
    idType: ID_TYPE.BIZ,
    idNo: '',
    ceoName: '',
    tel: '',
    email: '',
    address: '',
    status: VENDOR_STATUS.ACTIVE,
    useYn: 'Y',
    remark: '',
    sortOrder: 0,
    lastUsedAt: '',
    mod: '',
    ...partial,
  };
}

export function emptyAccount(partial = {}) {
  return {
    accountUid: '',
    vendorUid: '',
    bankCode: '',
    bankName: '',
    accountNo: '',
    accountHolder: '',
    mainYn: 'N',
    useYn: 'Y',
    remark: '',
    mod: '',
    ...partial,
  };
}

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

export function getCachedVendors(tenantCode) {
  return cacheGet(vendorCache, tenantCode) || [];
}

export function getCachedAccounts(tenantCode) {
  return cacheGet(accountCache, tenantCode) || [];
}

/** @returns {Promise<Array>} */
export async function loadVendors(tenantCode) {
  const tc = tenantCode || '';
  if (isBdApiEnabled()) {
    const rows = await commonSelect('CmVendor.selectVendorList', { tenantCode: tc });
    const mapped = (rows || []).map((r) => ({ ...r, mod: '' }));
    cacheSet(vendorCache, tc, mapped);
    return mapped;
  }
  const mapped = (readJson(vendorStorageKey(tc), []) || []).map((r) => ({ ...r, mod: '' }));
  cacheSet(vendorCache, tc, mapped);
  return mapped;
}

/** @returns {Promise<Array>} */
export async function loadAccounts(tenantCode) {
  const tc = tenantCode || '';
  if (isBdApiEnabled()) {
    const rows = await commonSelect('CmVendor.selectVendorAccountList', { tenantCode: tc });
    const mapped = (rows || []).map((r) => ({ ...r, mod: '' }));
    cacheSet(accountCache, tc, mapped);
    return mapped;
  }
  const mapped = (readJson(accountStorageKey(tc), []) || []).map((r) => ({ ...r, mod: '' }));
  cacheSet(accountCache, tc, mapped);
  return mapped;
}

/**
 * 거래처+계좌 일괄 저장 (A/U/D diff)
 * @returns {Promise<{vendors:Array, accounts:Array}>}
 */
export async function saveVendorBundle(tenantCode, vendors, accounts) {
  const tc = tenantCode || '';
  const nextVendors = (vendors || []).filter((v) => v && v.mod !== 'D');
  const nextAccounts = (accounts || []).filter((a) => a && a.mod !== 'D');

  if (isBdApiEnabled()) {
    const prevV = getCachedVendors(tc).length ? getCachedVendors(tc) : await loadVendors(tc);
    const prevA = getCachedAccounts(tc).length ? getCachedAccounts(tc) : await loadAccounts(tc);
    const dirtyV = diffRowsByUid(prevV, nextVendors, 'vendorUid', () => createVendorUid());
    // 계좌는 거래처 삭제 시 선행 삭제
    const deletedVendorUids = new Set(
      dirtyV.filter((r) => r.mod === 'D').map((r) => r.vendorUid)
    );
    const accountsForDiff = nextAccounts.filter((a) => !deletedVendorUids.has(a.vendorUid));
    const dirtyA = [
      ...diffRowsByUid(prevA, accountsForDiff, 'accountUid', () => createAccountUid()),
      ...prevA
        .filter((a) => deletedVendorUids.has(a.vendorUid))
        .map((a) => ({ ...a, mod: 'D' })),
    ];
    // FK: 계좌 삭제 → 거래처 삭제 → 거래처 추가/수정 → 계좌 추가/수정
    const delA = dirtyA.filter((r) => r.mod === 'D');
    const delV = dirtyV.filter((r) => r.mod === 'D');
    const upsV = dirtyV.filter((r) => r.mod === 'A' || r.mod === 'U');
    const upsA = dirtyA.filter((r) => r.mod === 'A' || r.mod === 'U');
    if (delA.length) await commonSaveList(SAVE_QUERY_VENDOR_ACCOUNT, delA, { tenantCode: tc });
    if (delV.length) await commonSaveList(SAVE_QUERY_VENDOR, delV, { tenantCode: tc });
    if (upsV.length) await commonSaveList(SAVE_QUERY_VENDOR, upsV, { tenantCode: tc });
    if (upsA.length) await commonSaveList(SAVE_QUERY_VENDOR_ACCOUNT, upsA, { tenantCode: tc });
    const vendorsOut = await loadVendors(tc);
    const accountsOut = await loadAccounts(tc);
    return { vendors: vendorsOut, accounts: accountsOut };
  }

  writeJson(vendorStorageKey(tc), nextVendors.map((v) => ({ ...v, mod: '' })));
  writeJson(accountStorageKey(tc), nextAccounts.map((a) => ({ ...a, mod: '' })));
  cacheSet(vendorCache, tc, nextVendors.map((v) => ({ ...v, mod: '' })));
  cacheSet(accountCache, tc, nextAccounts.map((a) => ({ ...a, mod: '' })));
  return { vendors: nextVendors, accounts: nextAccounts };
}

export function nextVendorCode(vendors = []) {
  const nums = (vendors || [])
    .map((v) => {
      const m = String(v.vendorCode || '').match(/^V(\d+)$/i);
      return m ? Number(m[1]) : 0;
    })
    .filter((n) => n > 0);
  const next = nums.length ? Math.max(...nums) + 1 : 1;
  return `V${String(next).padStart(5, '0')}`;
}

export function nextSortOrderBy10(rows = []) {
  const max = (rows || []).reduce((acc, r) => {
    const n = Number(r.sortOrder) || 0;
    return n > acc ? n : acc;
  }, 0);
  return max + 10;
}

export function enrichVendorRow(row) {
  const r = row || {};
  return {
    ...r,
    partyKindNm: partyKindLabel(r.partyKind),
    vendorTypeNm: vendorTypeLabel(r.vendorType),
    idTypeNm: idTypeLabel(r.idType),
    statusNm: vendorStatusLabel(r.status),
    idNoDisp:
      r.idType === ID_TYPE.BIZ && digitsOnly(r.idNo).length === 10
        ? formatBizNo(r.idNo)
        : r.idNo || '',
  };
}

/**
 * 조회 필터
 * - keyword: 코드·명·식별번호·대표자 부분일치
 */
export function filterVendors(vendors = [], filter = {}) {
  let list = (vendors || []).filter((v) => v.mod !== 'D');
  const status = String(filter.status || '').trim();
  if (status) list = list.filter((v) => v.status === status);
  const vendorType = String(filter.vendorType || '').trim();
  if (vendorType) list = list.filter((v) => v.vendorType === vendorType);
  const useYn = String(filter.useYn || '').trim();
  if (useYn) list = list.filter((v) => v.useYn === useYn);
  const kw = String(filter.keyword || '').trim().toLowerCase();
  if (kw) {
    const kwDigits = digitsOnly(kw);
    list = list.filter((v) => {
      const hay = [
        v.vendorCode,
        v.vendorName,
        v.ceoName,
        v.idNo,
        formatBizNo(v.idNo),
        v.tel,
      ]
        .map((s) => String(s || '').toLowerCase())
        .join(' ');
      if (hay.includes(kw)) return true;
      if (kwDigits && digitsOnly(v.idNo).includes(kwDigits)) return true;
      return false;
    });
  }
  return list;
}

export function accountsOfVendor(accounts = [], vendorUid) {
  const uid = String(vendorUid || '').trim();
  if (!uid) return [];
  return (accounts || []).filter((a) => a.vendorUid === uid && a.mod !== 'D');
}

/**
 * 저장 전 검증
 * @returns {string[]} 오류 메시지
 */
export function validateVendorBundle(vendors = [], accounts = []) {
  const errors = [];
  const active = (vendors || []).filter((v) => v.mod !== 'D');
  const idMap = new Map();

  active.forEach((v, idx) => {
    const label = v.vendorName || v.vendorCode || `#${idx + 1}`;
    if (!String(v.vendorName || '').trim()) {
      errors.push(`거래처명 필수: ${label}`);
    }
    const idNo = normalizeIdNo(v.idType, v.idNo);
    if (v.idType === ID_TYPE.BIZ && idNo) {
      if (!isValidBizNo(idNo)) {
        errors.push(`사업자번호 형식 오류: ${label} (${v.idNo})`);
      }
    }
    if (idNo && (v.status === VENDOR_STATUS.ACTIVE || v.status === VENDOR_STATUS.CANDIDATE)) {
      const key = `${v.idType || ''}|${idNo}`;
      if (idMap.has(key)) {
        errors.push(`동일 식별번호 중복: ${idNo} (${idMap.get(key)} / ${label})`);
      } else {
        idMap.set(key, label);
      }
    }
  });

  const byVendor = new Map();
  (accounts || [])
    .filter((a) => a.mod !== 'D')
    .forEach((a) => {
      const list = byVendor.get(a.vendorUid) || [];
      list.push(a);
      byVendor.set(a.vendorUid, list);
    });

  byVendor.forEach((list, vendorUid) => {
    const vendor = active.find((v) => v.vendorUid === vendorUid);
    const name = vendor?.vendorName || vendorUid;
    const mains = list.filter((a) => a.mainYn === 'Y');
    if (list.length && mains.length === 0) {
      errors.push(`대표계좌 없음: ${name}`);
    }
    if (mains.length > 1) {
      errors.push(`대표계좌는 1개만 허용: ${name}`);
    }
    list.forEach((a) => {
      if (!String(a.accountNo || '').trim()) {
        errors.push(`계좌번호 필수: ${name}`);
      }
    });
  });

  return errors;
}

/** 라벨 → 코드 (엑셀용) */
function codeFromLabel(options, raw, fallback = '') {
  const s = String(raw || '').trim();
  if (!s) return fallback;
  const byValue = options.find((o) => o.value === s);
  if (byValue) return byValue.value;
  const byLabel = options.find((o) => o.label === s);
  if (byLabel) return byLabel.value;
  return fallback || s;
}

function resolveVendorType(raw) {
  return codeFromLabel(VENDOR_TYPE_OPTIONS, raw, VENDOR_TYPE.OTHER);
}

function resolveIdType(raw) {
  return codeFromLabel(ID_TYPE_OPTIONS, raw, ID_TYPE.BIZ);
}

function resolveStatus(raw) {
  return codeFromLabel(VENDOR_STATUS_OPTIONS, raw, VENDOR_STATUS.ACTIVE);
}

function resolveYn(raw, fallback = 'Y') {
  const s = String(raw || '').trim().toUpperCase();
  if (s === 'Y' || s === 'N') return s;
  if (s === '예' || s === '대표') return 'Y';
  if (s === '아니오' || s === '') return fallback;
  return fallback;
}

/**
 * 평탄 엑셀 AOA → vendors + accounts
 * 그룹키: 식별유형+식별번호 (없으면 거래처코드, 없으면 거래처명)
 */
export function parseFlatVendorExcel(aoa, { tenantCode } = {}) {
  void tenantCode;
  const rows = Array.isArray(aoa) ? aoa : [];
  if (rows.length < 2) {
    return { vendors: [], accounts: [], errors: ['데이터가 없습니다.'] };
  }

  const header = (rows[0] || []).map((h) => String(h || '').trim());
  const col = (name) => header.indexOf(name);

  const idx = {
    vendorCode: col('거래처코드'),
    vendorName: col('거래처명'),
    vendorType: col('거래처구분'),
    idType: col('식별유형'),
    idNo: col('식별번호'),
    ceoName: col('대표자'),
    tel: col('전화'),
    email: col('이메일'),
    address: col('주소'),
    status: col('상태'),
    useYn: col('사용'),
    remark: col('비고'),
    bankCode: col('은행코드'),
    bankName: col('은행명'),
    accountNo: col('계좌번호'),
    accountHolder: col('예금주'),
    mainYn: col('대표계좌'),
    acctUseYn: col('계좌사용'),
    acctRemark: col('계좌비고'),
  };

  if (idx.vendorName < 0) {
    return { vendors: [], accounts: [], errors: ['필수 헤더「거래처명」이 없습니다.'] };
  }

  const cell = (row, i) => (i >= 0 ? String(row[i] ?? '').trim() : '');

  const groupMap = new Map();
  const errors = [];

  for (let r = 1; r < rows.length; r += 1) {
    const row = rows[r] || [];
    if (!row.some((c) => String(c ?? '').trim())) continue;

    const vendorName = cell(row, idx.vendorName);
    const vendorCode = cell(row, idx.vendorCode);
    let idType = resolveIdType(cell(row, idx.idType));
    let idNoRaw = cell(row, idx.idNo);
    let vendorType = resolveVendorType(cell(row, idx.vendorType));

    if (!vendorName && !idNoRaw && !vendorCode) {
      errors.push(`${r + 1}행: 거래처명·식별번호·코드 중 하나 필요`);
      continue;
    }

    // 사업자번호면 구분 자동 (엑셀에 구분이 비었을 때)
    if (idType === ID_TYPE.BIZ && idNoRaw) {
      const inferred = inferVendorTypeFromBizNo(idNoRaw);
      if (inferred) {
        if (!cell(row, idx.vendorType)) vendorType = inferred.vendorType;
        idType = inferred.idType;
      }
    }

    const idNo = normalizeIdNo(idType, idNoRaw);
    const groupKey =
      idNo && idType !== ID_TYPE.NONE
        ? `${idType}|${idNo}`
        : vendorCode
          ? `CODE|${vendorCode}`
          : `NAME|${vendorName}`;

    if (!groupMap.has(groupKey)) {
      const inferred = idType === ID_TYPE.BIZ && idNo ? inferVendorTypeFromBizNo(idNo) : null;
      groupMap.set(groupKey, {
        vendor: emptyVendor({
          vendorUid: createVendorUid(),
          vendorCode: vendorCode || '',
          vendorName: vendorName || vendorCode || idNo,
          vendorType: vendorType || inferred?.vendorType || VENDOR_TYPE.OTHER,
          partyKind:
            inferred?.partyKind ||
            (vendorType === VENDOR_TYPE.INDIVIDUAL ? PARTY_KIND.PERSON : PARTY_KIND.BIZ),
          vendorTypeSource: cell(row, idx.vendorType) ? 'MANUAL' : inferred ? 'AUTO_BIZNO' : 'MANUAL',
          idType,
          idNo,
          ceoName: cell(row, idx.ceoName),
          tel: cell(row, idx.tel),
          email: cell(row, idx.email),
          address: cell(row, idx.address),
          status: resolveStatus(cell(row, idx.status)),
          useYn: resolveYn(cell(row, idx.useYn), 'Y'),
          remark: cell(row, idx.remark),
          mod: 'A',
        }),
        accounts: [],
      });
    }

    const g = groupMap.get(groupKey);
    const accountNo = cell(row, idx.accountNo);
    if (accountNo) {
      g.accounts.push(
        emptyAccount({
          accountUid: createAccountUid(),
          vendorUid: g.vendor.vendorUid,
          bankCode: cell(row, idx.bankCode),
          bankName: cell(row, idx.bankName),
          accountNo,
          accountHolder: cell(row, idx.accountHolder) || g.vendor.vendorName,
          mainYn: resolveYn(cell(row, idx.mainYn), g.accounts.length === 0 ? 'Y' : 'N'),
          useYn: resolveYn(cell(row, idx.acctUseYn), 'Y'),
          remark: cell(row, idx.acctRemark),
          mod: 'A',
        })
      );
    }
  }

  const vendors = [];
  const accounts = [];
  let sort = 10;
  groupMap.forEach((g) => {
    if (!g.vendor.vendorCode) {
      g.vendor.vendorCode = nextVendorCode([...vendors, g.vendor]);
    }
    g.vendor.sortOrder = sort;
    sort += 10;
    // 계좌 있으면 대표 1개 보장
    if (g.accounts.length) {
      const mains = g.accounts.filter((a) => a.mainYn === 'Y');
      if (!mains.length) g.accounts[0].mainYn = 'Y';
      if (mains.length > 1) {
        g.accounts.forEach((a, i) => {
          a.mainYn = i === 0 ? 'Y' : 'N';
        });
      }
    }
    vendors.push(g.vendor);
    accounts.push(...g.accounts);
  });

  return { vendors, accounts, errors };
}

/** 양식 다운로드 (샘플 2행: 동일 사업자 2계좌) */
export function downloadVendorExcelTemplate(fileName = '거래처업로드양식.xlsx') {
  const sample = [
    VENDOR_FLAT_EXCEL_HEADERS,
    [
      'V00001',
      '샘플상사',
      '영리법인',
      '사업자번호',
      '123-81-67890',
      '홍길동',
      '02-1234-5678',
      'sample@example.com',
      '서울시 중구',
      '사용',
      'Y',
      '본점',
      '004',
      '국민',
      '12345678901234',
      '샘플상사',
      'Y',
      'Y',
      '',
    ],
    [
      'V00001',
      '샘플상사',
      '영리법인',
      '사업자번호',
      '123-81-67890',
      '홍길동',
      '02-1234-5678',
      'sample@example.com',
      '서울시 중구',
      '사용',
      'Y',
      '본점',
      '088',
      '신한',
      '110123456789',
      '샘플상사',
      'N',
      'Y',
      '급여계좌',
    ],
  ];
  const ws = XLSX.utils.aoa_to_sheet(sample);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, '거래처');
  XLSX.writeFile(wb, fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`);
}

/** ArrayBuffer → AOA */
export function readExcelAoaFromArrayBuffer(buf) {
  const wb = XLSX.read(buf, { type: 'array' });
  const name = wb.SheetNames[0];
  const ws = wb.Sheets[name];
  return XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
}

/**
 * 업로드 결과를 기존 번들에 병합
 * - 동일 식별번호: 헤더 갱신(U) + 계좌는 번호 기준 추가/갱신
 * - 신규: A
 */
export function mergeUploadedVendors(existingVendors, existingAccounts, uploadedVendors, uploadedAccounts) {
  const vendors = [...(existingVendors || [])];
  const accounts = [...(existingAccounts || [])];
  const uidByNatural = new Map();

  vendors.forEach((v) => {
    if (v.mod === 'D') return;
    const idNo = normalizeIdNo(v.idType, v.idNo);
    if (idNo) uidByNatural.set(`${v.idType}|${idNo}`, v.vendorUid);
    if (v.vendorCode) uidByNatural.set(`CODE|${v.vendorCode}`, v.vendorUid);
  });

  const uidRemap = new Map();

  (uploadedVendors || []).forEach((up) => {
    const idNo = normalizeIdNo(up.idType, up.idNo);
    const naturalKey = idNo ? `${up.idType}|${idNo}` : '';
    const codeKey = up.vendorCode ? `CODE|${up.vendorCode}` : '';
    const existUid =
      (naturalKey && uidByNatural.get(naturalKey)) ||
      (codeKey && uidByNatural.get(codeKey)) ||
      '';

    if (existUid) {
      uidRemap.set(up.vendorUid, existUid);
      const idx = vendors.findIndex((v) => v.vendorUid === existUid);
      if (idx >= 0) {
        vendors[idx] = {
          ...vendors[idx],
          ...up,
          vendorUid: existUid,
          vendorCode: vendors[idx].vendorCode || up.vendorCode,
          mod: vendors[idx].mod === 'A' ? 'A' : 'U',
        };
      }
    } else {
      uidRemap.set(up.vendorUid, up.vendorUid);
      if (!up.vendorCode) up.vendorCode = nextVendorCode(vendors);
      vendors.push({ ...up, mod: 'A' });
      if (idNo) uidByNatural.set(`${up.idType}|${idNo}`, up.vendorUid);
      if (up.vendorCode) uidByNatural.set(`CODE|${up.vendorCode}`, up.vendorUid);
    }
  });

  (uploadedAccounts || []).forEach((up) => {
    const vendorUid = uidRemap.get(up.vendorUid) || up.vendorUid;
    const existIdx = accounts.findIndex(
      (a) =>
        a.mod !== 'D' &&
        a.vendorUid === vendorUid &&
        digitsOnly(a.accountNo) === digitsOnly(up.accountNo)
    );
    if (existIdx >= 0) {
      accounts[existIdx] = {
        ...accounts[existIdx],
        ...up,
        accountUid: accounts[existIdx].accountUid,
        vendorUid,
        mod: accounts[existIdx].mod === 'A' ? 'A' : 'U',
      };
    } else {
      accounts.push({
        ...up,
        accountUid: createAccountUid(),
        vendorUid,
        mod: 'A',
      });
    }
  });

  return { vendors, accounts };
}

/** PoC 시드 — 기관별 최초 1회 (API 모드에서는 DB만 사용, 시드 생략) */
export async function ensureVendorSeed(tenantCode) {
  if (isBdApiEnabled()) {
    const vendors = await loadVendors(tenantCode);
    const accounts = await loadAccounts(tenantCode);
    return { vendors, accounts };
  }
  const existing = await loadVendors(tenantCode);
  if (existing.length) {
    return { vendors: existing, accounts: await loadAccounts(tenantCode) };
  }

  const v1 = emptyVendor({
    vendorUid: createVendorUid(),
    vendorCode: 'V00001',
    vendorName: '한국사무용품',
    partyKind: PARTY_KIND.BIZ,
    vendorType: VENDOR_TYPE.CORP_PROFIT,
    vendorTypeSource: 'AUTO_BIZNO',
    idType: ID_TYPE.BIZ,
    idNo: '1208101234',
    ceoName: '김대표',
    tel: '02-111-2222',
    status: VENDOR_STATUS.ACTIVE,
    useYn: 'Y',
    sortOrder: 10,
    remark: '시드',
  });
  // 체크 통과하는 임의 번호가 아니면 검증에서 걸릴 수 있음 — 형식만 맞춤용으로 저장 시 완화하거나 유효번호 사용
  // PoC: 검증은 사용자가 수정 가능하게, 시드는 체크 통과 번호 생성 어려우므로 idNo를 비우고 명만
  v1.idNo = '';
  v1.vendorTypeSource = 'MANUAL';

  const v2 = emptyVendor({
    vendorUid: createVendorUid(),
    vendorCode: 'V00002',
    vendorName: '홍길동(강사)',
    partyKind: PARTY_KIND.PERSON,
    vendorType: VENDOR_TYPE.INDIVIDUAL,
    vendorTypeSource: 'MANUAL',
    idType: ID_TYPE.NONE,
    idNo: '',
    ceoName: '홍길동',
    tel: '010-1234-5678',
    status: VENDOR_STATUS.ACTIVE,
    useYn: 'Y',
    sortOrder: 20,
    remark: '1회성 개인 — 식별번호 없음',
  });

  const a1 = emptyAccount({
    accountUid: createAccountUid(),
    vendorUid: v1.vendorUid,
    bankCode: '004',
    bankName: '국민',
    accountNo: '12345678901234',
    accountHolder: '한국사무용품',
    mainYn: 'Y',
    useYn: 'Y',
  });
  const a2 = emptyAccount({
    accountUid: createAccountUid(),
    vendorUid: v1.vendorUid,
    bankCode: '088',
    bankName: '신한',
    accountNo: '110123456789',
    accountHolder: '한국사무용품',
    mainYn: 'N',
    useYn: 'Y',
    remark: '부계좌',
  });
  const a3 = emptyAccount({
    accountUid: createAccountUid(),
    vendorUid: v2.vendorUid,
    bankCode: '020',
    bankName: '우리',
    accountNo: '1002123456789',
    accountHolder: '홍길동',
    mainYn: 'Y',
    useYn: 'Y',
  });

  const vendors = [v1, v2];
  const accounts = [a1, a2, a3];
  await saveVendorBundle(tenantCode, vendors, accounts);
  return { vendors, accounts };
}
